/**
 * BattleRoom Configuration Constants
 * 
 * @date_of_change: 2025-09-07
 * @purpose: Centralize hardcoded values from BattleRoom.js to improve maintainability
 * @rule_reference: AGENTS.md - eliminate hardcoded values violations
 * 
 * This file contains all the magic numbers, paths, and configuration values
 * that were previously scattered throughout BattleRoom.js
 */

// Layout and UI Constants
export const LAYOUT_CONSTANTS = {
  SIDEBAR_WIDTH: {
    DEFAULT_LEFT: 300,
    DEFAULT_RIGHT: 350,
    COLLAPSED: 40,
    MIN_STAGE_WIDTH: 300
  },
  TOOLBAR_HEIGHT: 60,
  RESIZE_DEBOUNCE_MS: 100
};

// D&D 5e Game Mechanics Constants
export const DND_CONSTANTS = {
  FEET_PER_CELL: 5,
  DEFAULT_GRID_SIZE: 50, // pixels per grid cell
  MIN_TOKEN_SIZE: 25, // minimum width/height for tokens
  DEFAULT_SPEED: 30 // feet
};

// Asset and File Paths
export const ASSET_PATHS = {
  DEFAULT_MAP: '/assets/placeholders/default-grid.svg',
  TEXTURE_DIRECTORY: '/assets/textures/',
  MAPS_DIRECTORY: '/assets/maps/'
};

// Grid System Configuration
export const GRID_CONFIG = {
  TYPES: {
    SQUARE: 'square',
    HEX: 'hex'
  },
  DEFAULT_COLOR: '#000000',
  DEFAULT_OPACITY: 0.3,
  SNAP_THRESHOLD: 10 // pixels
};

// Token Configuration
export const TOKEN_CONFIG = {
  BORDER_COLORS: {
    PLAYER: '#4299e1',    // Blue
    MONSTER: '#e53e3e',   // Red
    NPC: '#38a169',       // Green
    OBJECT: '#d69e2e',    // Yellow
    DEFAULT: '#a0aec0'    // Gray
  },
  LAYERS: {
    MAP: 'map',
    TOKEN: 'token',
    GAMEMASTER: 'gamemaster',
    OVERLAY: 'overlay'
  },
  TARGET_HIGHLIGHT_COLOR: '#f6e05e', // Yellow for targeting
  SELECTION_BORDER_WIDTH: 2,
  TARGET_BORDER_WIDTH: 3
};

// Combat System Constants
export const COMBAT_CONSTANTS = {
  ACTION_TYPES: {
    ACTION: 'action',
    BONUS_ACTION: 'bonus_action',
    REACTION: 'reaction',
    LEGENDARY_ACTION: 'legendary_action'
  },
  DAMAGE_TYPES: [
    'acid', 'bludgeoning', 'cold', 'fire', 'force',
    'lightning', 'necrotic', 'piercing', 'poison',
    'psychic', 'radiant', 'slashing', 'thunder'
  ]
};

// Animation and Performance Constants
export const PERFORMANCE_CONFIG = {
  TRANSFORMER_ATTACH_DELAY: 50, // ms
  DEBOUNCE_RESIZE: 100, // ms
  MAX_CONSOLE_LOGS: 100, // prevent log spam
  MEMOIZATION_DEPS_LIMIT: 15 // warn if useMemo deps exceed this
};

// Measurement Tool Constants
export const MEASUREMENT_CONFIG = {
  LINE_COLOR: '#ff6b6b',
  LINE_WIDTH: 2,
  TEXT_COLOR: '#333333',
  TEXT_SIZE: 14,
  ARROW_SIZE: 8
};

// Export combined configuration for easy access
export const BATTLEROOM_CONFIG = {
  LAYOUT: LAYOUT_CONSTANTS,
  DND: DND_CONSTANTS,
  ASSETS: ASSET_PATHS,
  GRID: GRID_CONFIG,
  TOKEN: TOKEN_CONFIG,
  COMBAT: COMBAT_CONSTANTS,
  PERFORMANCE: PERFORMANCE_CONFIG,
  MEASUREMENT: MEASUREMENT_CONFIG
};

// Utility function to get stage dimensions based on sidebar states
export const calculateStageDimensions = (windowWidth, windowHeight, widths) => {
  const leftWidth = widths.leftCollapsed ? LAYOUT_CONSTANTS.SIDEBAR_WIDTH.COLLAPSED : widths.left;
  const rightWidth = widths.rightCollapsed ? LAYOUT_CONSTANTS.SIDEBAR_WIDTH.COLLAPSED : widths.right;
  
  return {
    width: Math.max(
      LAYOUT_CONSTANTS.SIDEBAR_WIDTH.MIN_STAGE_WIDTH,
      windowWidth - leftWidth - rightWidth
    ),
    height: windowHeight - LAYOUT_CONSTANTS.TOOLBAR_HEIGHT
  };
};

// Utility function to get token border color by type
export const getTokenBorderColor = (tokenType) => {
  switch(tokenType) {
    case 'player':
      return TOKEN_CONFIG.BORDER_COLORS.PLAYER;
    case 'monster':
      return TOKEN_CONFIG.BORDER_COLORS.MONSTER;
    case 'npc':
      return TOKEN_CONFIG.BORDER_COLORS.NPC;
    case 'object':
      return TOKEN_CONFIG.BORDER_COLORS.OBJECT;
    default:
      return TOKEN_CONFIG.BORDER_COLORS.DEFAULT;
  }
};
